import React from 'react';
import {
  Flex,
  Spinner,
  Box,
  Table,
  TableContainer,
  Tbody,
  Td,
  Th,
  Thead,
  Tr,
  Badge,
  Tooltip,
  Progress,
} from '@chakra-ui/react';
import {
  createColumnHelper,
  useReactTable,
  getCoreRowModel,
  flexRender,
} from '@tanstack/react-table';
import {GetWebIntentCustomers} from '../../shared/api/definitions/debug';
import {useGetWebIntentCustomers} from './hooks/customer';
import {ExtractResponseData} from '../../shared/api/definitions';

type WebIntentCustomer = ExtractResponseData<
  typeof GetWebIntentCustomers
>[number];

const getStatusInfo = (customer: WebIntentCustomer) => {
  if (customer.firstDataReceivedAt) {
    return {
      label: 'Data Received',
      colorScheme: 'green',
      tooltip: 'Intent data is being received from visitors',
    };
  }
  if (customer.trackingScriptDetectedAt) {
    return {
      label: 'Verified',
      colorScheme: 'blue',
      tooltip: 'Tracking script detected on website, awaiting first data',
    };
  }
  return {
    label: 'Enabled',
    colorScheme: 'yellow',
    tooltip:
      'Integration enabled, tracking script not yet detected and no data received',
  };
};

const columnHelper = createColumnHelper<WebIntentCustomer>();
const columns = [
  columnHelper.accessor('name', {
    header: 'Customer',
    cell: (info) => info.getValue(),
  }),
  columnHelper.accessor((row: WebIntentCustomer) => getStatusInfo(row), {
    id: 'status',
    header: 'Status',
    cell: (info) => {
      const {label, colorScheme, tooltip} = info.getValue();
      return (
        <Tooltip label={tooltip} placement="top">
          <Badge colorScheme={colorScheme}>{label}</Badge>
        </Tooltip>
      );
    },
  }),
  columnHelper.accessor('webIntentIdentificationsPerMonth', {
    header: 'Monthly Usage',
    cell: (info) => {
      const {current, limit} = info.getValue();
      const percentage = limit > 0 ? Math.min((current / limit) * 100, 100) : 0;
      const color =
        percentage >= 90
          ? 'kred.400'
          : percentage >= 70
            ? 'korange.400'
            : 'kblue.400';
      return (
        <Flex alignItems="center" gap={3}>
          <Box minW="100px">
            <Progress
              value={percentage}
              size="sm"
              borderRadius="full"
              bg="kgray.200"
              sx={{'& > div': {bg: color}}}
            />
          </Box>
          <Box fontSize="sm" color="kgray.400" whiteSpace="nowrap">
            {current.toLocaleString()} / {limit.toLocaleString()}
          </Box>
        </Flex>
      );
    },
  }),
];

const IntentRolloutTable = ({customers}: {customers: WebIntentCustomer[]}) => {
  const table = useReactTable({
    data: customers,
    columns,
    getCoreRowModel: getCoreRowModel(),
  });

  return (
    <TableContainer>
      <Table variant="striped">
        <Thead>
          {table.getHeaderGroups().map((headerGroup) => (
            <Tr key={headerGroup.id}>
              {headerGroup.headers.map((header) => (
                <Th key={header.id}>
                  {header.isPlaceholder
                    ? null
                    : flexRender(
                        header.column.columnDef.header,
                        header.getContext()
                      )}
                </Th>
              ))}
            </Tr>
          ))}
        </Thead>
        <Tbody>
          {table.getRowModel().rows.map((row) => (
            <Tr key={row.id}>
              {row.getVisibleCells().map((cell) => (
                <Td key={cell.id}>
                  {flexRender(cell.column.columnDef.cell, cell.getContext())}
                </Td>
              ))}
            </Tr>
          ))}
        </Tbody>
      </Table>
    </TableContainer>
  );
};

export const IntentRollout = () => {
  const {data, isPending} = useGetWebIntentCustomers({});

  return (
    <Box p={8}>
      <Box fontSize="2xl" mb={8}>
        Intent Rollout
      </Box>
      <Flex direction="column">
        {isPending && <Spinner />}
        {data && <IntentRolloutTable customers={data} />}
      </Flex>
    </Box>
  );
};
